/* eslint-disable */
// @ts-nocheck
/* ============================================================================
 * MTE — AudioButton component (M-C2.4 smoke fix 1 — 2026-09-20)
 * ----------------------------------------------------------------------------
 * Spec : mte-pedagogy/mte-diagnostic-banks-by-target-level-v1.md v2.1 §7
 *
 * 2 types audio distincts :
 *   - instruction_audio = apoio à leitura da consigne (CP/CE1/CE2 bouton · CM1/CM2 absent)
 *   - stimulus_audio     = parte da atividade (exige ouvir) — sempre bouton si required
 *
 * Block 9 (2026-09) — front funcional áudio:
 *   - TTS fallback cuando audioUrl falha (onerror / play().catch) → speechSynthesis
 *   - State hasPlayed para label "Réécouter" após primeira reprodução
 *
 * Pas d'autoplay. TTS via window.speechSynthesis natif OU audio pre-recorded.
 * ========================================================================== */

import React, { useState, useRef } from 'react';

export interface AudioButtonProps {
  /** Texto a ser falado (TTS fallback) */
  text: string;
  /** URL alternativa audio pre-recorded (opcional) */
  audioUrl?: string;
  /** Label acessível */
  ariaLabel?: string;
  /** Type d'audio (visuellement distinctif optionnel) */
  kind?: 'instruction' | 'stimulus';
  /** Estilo adicional */
  className?: string;
}

/**
 * TTS fallback : fala `text` em fr-FR rate 0.85 via window.speechSynthesis.
 * Usado quando audioUrl falha (onerror / play().catch).
 */
function speakTTS(text: string, onEnd?: () => void): void {
  if (typeof window === 'undefined' || !window.speechSynthesis) {
    onEnd?.();
    return;
  }
  const u = new SpeechSynthesisUtterance(text);
  u.lang = 'fr-FR';
  u.rate = 0.85;
  if (onEnd) u.onend = onEnd;
  window.speechSynthesis.speak(u);
}

export function AudioButton({
  text,
  audioUrl,
  ariaLabel,
  kind = 'instruction',
  className,
}: AudioButtonProps): JSX.Element {
  const [playing, setPlaying] = useState<boolean>(false);
  const [hasPlayed, setHasPlayed] = useState<boolean>(false);
  const synthRef = useRef<SpeechSynthesisUtterance | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Evitar double TTS fallback (onerror E play().catch podem disparar para 404)
  const fallbackFiredRef = useRef<boolean>(false);

  const triggerTTSFallback = () => {
    if (fallbackFiredRef.current) return;
    fallbackFiredRef.current = true;
    speakTTS(text, () => {
      setHasPlayed(true);
      setPlaying(false);
    });
  };

  const stopAll = () => {
    if (synthRef.current && typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlaying(false);
  };

  const handlePlay = () => {
    // Reset dedup ref para novo clique
    fallbackFiredRef.current = false;

    if (playing) {
      // Stop
      stopAll();
      return;
    }
    if (audioUrl) {
      const aud = new Audio(audioUrl);
      audioRef.current = aud;
      aud.onended = () => {
        setHasPlayed(true);
        setPlaying(false);
      };
      aud.onerror = () => {
        // Asset indisponível (404, réseau coupé, decode error…) → TTS fallback
        triggerTTSFallback();
      };
      aud.play().catch(() => {
        // play() rejeitou (autoplay bloqueado, URL inválida, etc.) → TTS fallback
        triggerTTSFallback();
      });
    } else if (typeof window !== 'undefined' && window.speechSynthesis) {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = 'fr-FR';
      u.rate = 0.85;
      u.onend = () => {
        setHasPlayed(true);
        setPlaying(false);
      };
      u.onerror = () => {
        setHasPlayed(true);
        setPlaying(false);
      };
      synthRef.current = u;
      window.speechSynthesis.speak(u);
    }
    setHasPlayed(true);
    setPlaying(true);
  };

  const visibleLabel = playing
    ? 'Arrêter'
    : !hasPlayed
    ? kind === 'stimulus'
      ? "Écouter l'audio"
      : 'Écouter la consigne'
    : kind === 'stimulus'
    ? "Réécouter l'audio"
    : 'Réécouter la consigne';

  const ariaLabelText = playing
    ? `Arrêter ${kind === 'stimulus' ? 'le son' : "la lecture de la consigne"}`
    : !hasPlayed
    ? kind === 'stimulus'
      ? "Écouter l'audio"
      : 'Écouter la consigne'
    : kind === 'stimulus'
    ? "Réécouter l'audio"
    : 'Réécouter la consigne';

  return (
    <button
      type="button"
      className={`mte-audio-button mte-audio-${kind}${className ? ' ' + className : ''}`}
      data-testid={`audio-button-${kind}`}
      data-kind={kind}
      data-playing={playing ? 'true' : 'false'}
      data-has-played={hasPlayed ? 'true' : 'false'}
      onClick={handlePlay}
      aria-label={ariaLabel ?? ariaLabelText}
    >
      <span aria-hidden="true">{playing ? '⏹' : kind === 'stimulus' ? '🔊' : '🔈'}</span>
      <span className="mte-audio-button-label">{visibleLabel}</span>
    </button>
  );
}

export default AudioButton;
